/**
 * Which home page a visitor gets, decided by the domain they arrived on.
 *
 * I run one platform on two domains: vault-app.co.za keeps the South African
 * home page, and vaultpayday.com shows the global waitlist until Vault opens in
 * other countries. The check lives here, apart from the pages, so it can be
 * tested and so local runs can preview the global page with ?site=global
 * (only on localhost, never on a live domain).
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */

export const GLOBAL_DOMAIN = 'vaultpayday.com';

/** True when the hostname is the global domain or one of its subdomains (www). */
export function isGlobalHost(hostname) {
  const h = String(hostname || '').toLowerCase().replace(/\.$/, '');
  return h === GLOBAL_DOMAIN || h.endsWith(`.${GLOBAL_DOMAIN}`);
}

/** 'global' or 'sa' for a location-like object ({ hostname, search }). */
export function siteFor(loc) {
  const host = String(loc?.hostname || '').toLowerCase();
  if (isGlobalHost(host)) return 'global';
  const local = host === 'localhost' || host === '127.0.0.1';
  if (local && new URLSearchParams(loc?.search || '').get('site') === 'global') return 'global';
  return 'sa';
}

// Every assigned ISO 3166-1 alpha-2 code, so a visitor from any country can say
// where they are. Names come from the browser (Intl.DisplayNames), never typed here.
export const COUNTRY_CODES = (
  'AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ '
  + 'CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET '
  + 'FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU '
  + 'ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY '
  + 'MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM '
  + 'PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ '
  + 'TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW'
).split(' ');

/** [{ code, name }] sorted by name, using the browser's own country names. */
export function countryOptions(locale = 'en') {
  let names = null;
  try { names = new Intl.DisplayNames([locale], { type: 'region' }); } catch { /* old browser */ }
  return COUNTRY_CODES
    .map((code) => ({ code, name: (names && names.of(code)) || code }))
    .sort((a, b) => a.name.localeCompare(b.name, locale));
}

/** The region part of a language tag ('en-ZW' → 'ZW'), if it is a known code. */
export function guessCountry(languages = []) {
  for (const tag of languages) {
    const m = /-([A-Za-z]{2})$/.exec(String(tag || ''));
    if (m && COUNTRY_CODES.includes(m[1].toUpperCase())) return m[1].toUpperCase();
  }
  return '';
}

// Whether the signed-in account may see the SARS tax pack. The server is the
// authority (tax_pack_available on /auth/me, and the tax endpoints refuse other
// countries); this mirror only hides the menu items, cards and ticks that would
// otherwise lead to a refusal. An older payload without the field means a South
// African account, as every account was before the field existed.
let taxOk = true;
export function setAccountRegion(me) { taxOk = me ? me.tax_pack_available !== false : true; }
export const taxPackAvailable = () => taxOk;
